import { Dialog, DialogRef } from '@angular/cdk/dialog'
import { createGlobalPositionStrategy } from '@angular/cdk/overlay'
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  NgZone,
  TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core'

import { TooltipModule } from 'primeng/tooltip'

import { FlxButtonModule, FlxFormControlModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { TOOLTIP_DEFAULT_DELAY } from '@flowx/designer/modules/core/dictionary/constants'
import { WorkspaceType } from '@flowx/designer/modules/workspaces/shared/commons.types'
import { WorkspaceEnvTypeTagComponent } from '@flowx/designer/modules/workspaces/shared/workspace-env-type-tag/workspace-env-type-tag.component'
import { noResultsCaption } from '@flowx/designer/shared/components/empty-infobox/empty-state-pattern'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'

import { FlxNotificationCenterItemComponent } from './notification-center-item/notification-center-item.component'
import {
  NOTIFICATION_RETENTION_DAYS,
  NotificationFilter,
  NotificationFilterMode,
  groupNotificationsByDate,
} from './notifications-center.model'
import { NotificationsCenterStore } from './notifications-center.store'

import { ExtendedNotification, actionOpensModal } from '../notifications.model'
import { NotificationsService } from '../services/notifications.service'

/** The minute is the finest unit `relativeAgeLabel` renders, so a faster tick repaints identical text. */
const AGE_REFRESH_INTERVAL_MS = 60_000

/** An open panel renders every arrival itself, so the toast stack stays quiet while it is up. */
const TOAST_SUPPRESSOR = 'notifications-center'

/**
 * The notification bell and the panel it opens. Self-contained on purpose — it reads the root store
 * and nothing else, so moving it out of the application banner and into the global chrome is a
 * one-line change at the mount point.
 */
@Component({
  selector: 'flx-notifications-center',
  imports: [
    TooltipModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxIconModule,
    FlxSpinnerModule,
    FlxNotificationCenterItemComponent,
    WorkspaceEnvTypeTagComponent,
  ],
  templateUrl: './notifications-center.component.html',
  styleUrl: './notifications-center.component.scss',
})
export class FlxNotificationsCenterComponent {
  #dialog = inject(Dialog)
  #injector = inject(Injector)
  #viewContainerRef = inject(ViewContainerRef)
  #zone = inject(NgZone)
  #notificationsService = inject(NotificationsService)
  #store = inject(NotificationsCenterStore)

  #panelRef?: DialogRef
  #ticker?: ReturnType<typeof setInterval>

  NOTIFICATION_RETENTION_DAYS = NOTIFICATION_RETENTION_DAYS
  NotificationFilterMode = NotificationFilterMode
  TOOLTIP_DEFAULT_DELAY = TOOLTIP_DEFAULT_DELAY
  WORKSPACE_TYPES = Object.values(WorkspaceType)

  filter = this.#store.filter
  isFiltering = this.#store.isFiltering
  loading = this.#store.loading
  search = this.#store.search
  unreadCount = this.#store.unreadCount

  bellButton = viewChild.required('bellButton', { read: ElementRef<HTMLButtonElement> })
  panelTemplate = viewChild.required<TemplateRef<unknown>>('panel')

  currentTime = signal(Date.now())
  filterOpen = signal(false)

  groups = computed(() => groupNotificationsByDate(this.#store.filtered(), this.currentTime()))

  bellAccessibleName = computed(() =>
    this.unreadCount() > 0 ? `Notifications, ${this.unreadCount()} unread` : 'Notifications'
  )

  filterButtonLabel = computed(() => (this.isFiltering() ? 'Filter applied' : 'Filter'))

  /**
   * Order is load-bearing: `isFiltering` is true for `UNREAD` as well, so that branch must come
   * first, and an active filter outranks an active search by product decision.
   */
  emptyMessage = computed(() => {
    if (this.#store.notifications().length === 0) {
      return 'No notifications yet.'
    }

    if (this.#store.filter() === NotificationFilterMode.UNREAD) {
      return 'No unread notifications.'
    }

    if (this.#store.isFiltering()) {
      return 'No notifications for this filter.'
    }

    return noResultsCaption('notifications')
  })

  constructor() {
    // The panel lives in the overlay container, so it would outlive this component — and keep the
    // ticker running and the toast stack muted — unless it is closed here.
    inject(DestroyRef).onDestroy(() => this.closePanel())
  }

  /**
   * The panel sits under the global header, but its backdrop covers the whole viewport, header and
   * footer included: a click anywhere outside the panel closes it rather than reaching the control
   * underneath. That includes the bell, so the bell only ever opens it.
   */
  openPanel(): void {
    if (this.#panelRef) {
      return
    }

    this.#panelRef = this.#dialog.open(this.panelTemplate(), {
      viewContainerRef: this.#viewContainerRef,
      positionStrategy: createGlobalPositionStrategy(this.#injector)
        .top('var(--flx-header-height)')
        .right('0'),
      width: '400px',
      // Down to the footer, on pages that show one.
      height: 'calc(100% - var(--flx-header-height) - var(--flx-footer-inset))',
      backdropClass: 'cdk-overlay-transparent-backdrop',
      ariaLabel: 'Notifications',
      ariaModal: true,
      restoreFocus: this.bellButton().nativeElement,
    })

    this.#panelRef.closed.subscribe(() => this.#onPanelClosed())

    this.#notificationsService.addToastSuppressor(TOAST_SUPPRESSOR)
    this.#notificationsService.dismissAll()
    this.filterOpen.set(false)
    this.currentTime.set(Date.now())

    this.#ticker = this.#zone.runOutsideAngular(() =>
      setInterval(() => this.currentTime.set(Date.now()), AGE_REFRESH_INTERVAL_MS)
    )
  }

  closePanel(): void {
    this.#panelRef?.close()
  }

  onSearchInput(event: Event): void {
    this.#store.setSearch((event.target as HTMLInputElement).value)
  }

  selectFilter(filter: NotificationFilter): void {
    this.#store.setFilter(filter)
    this.filterOpen.set(false)
  }

  setMarkedRead(notificationId: string, markedRead: boolean): void {
    void this.#store.setMarkedRead(notificationId, markedRead)
  }

  markAllAsRead(): void {
    void this.#store.markAllAsRead()
  }

  /** Clicking a notification's action reads it, as a toast does, and closes the panel unless the action opens a modal over it. */
  onActionClicked(notification: ExtendedNotification): void {
    if (!notification.markedRead) {
      this.setMarkedRead(notification.id, true)
    }

    if (!actionOpensModal(notification)) {
      this.closePanel()
    }
  }

  #onPanelClosed(): void {
    this.#panelRef = undefined
    this.#notificationsService.removeToastSuppressor(TOAST_SUPPRESSOR)
    this.#stopTicking()
  }

  #stopTicking(): void {
    clearInterval(this.#ticker)
    this.#ticker = undefined
  }
}
